<script setup lang="ts">
import { BChip, BIcon } from '@/design-system/components';
import { useTranslation } from '@/composables';
import type { AntennaReadingType } from '@/types/AntennaReadingType';

type PropsType = {
    reading: AntennaReadingType;
    showAntennaName?: boolean;
    maxEpcs?: number;
    antennaName?: string;
    readerName?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    showAntennaName: false,
    maxEpcs: 10,
    antennaName: '',
    readerName: '',
});

const { t } = useTranslation();

const displayEpcs = (reading: AntennaReadingType) => {
    if (!reading.readings || reading.readings.length === 0) {
        return [];
    }

    if (props.maxEpcs === undefined || props.maxEpcs <= 0) {
        return reading.readings;
    }

    return reading.readings.slice(0, props.maxEpcs);
};
</script>

<template>
    <div class="log-item">
        <div class="log-time">
            {{ reading.timestamp }}
        </div>
        <div class="log-content">
            <div class="d-flex align-center ga-2 mb-1 flex-wrap">
                <template v-if="showAntennaName">
                    <strong>{{ readerName }} - {{ antennaName }}</strong>
                    <span>{{ t('antennas.loaded') }}</span>
                </template>
                <BChip
                    color="primary"
                    size="x-small"
                >
                    {{ reading.readings?.length || 0 }} {{ t('antennas.epc') }}
                </BChip>
                <BIcon
                    icon="mdi-tag-multiple"
                    size="small"
                    class="text-medium-emphasis"
                />
            </div>
            <div class="epc-list">
                <div
                    v-for="(epc, epcIndex) in displayEpcs(reading)"
                    :key="epcIndex"
                    class="epc-item"
                >
                    {{ epc }}
                </div>
                <div
                    v-if="reading.readings?.length > maxEpcs"
                    class="text-caption text-medium-emphasis"
                >
                    {{ t('antennas.moreEpcs', { count: reading.readings.length - maxEpcs }) }}
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.log-item {
    display: flex;
    flex-flow: row wrap;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);

    &:last-child {
        border-bottom: none;
    }
}

.log-time {
    font-size: 0.75rem;
    color: rgb(var(--v-theme-grey-900));
    min-width: 140px;
    font-weight: 500;
    flex-shrink: 0;
}

.log-content {
    flex: 1;
}

.epc-list {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 4px;
}

.epc-item {
    background: rgba(var(--v-theme-primary), 0.1);
    color: rgb(var(--v-theme-primary));
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-family: monospace;
}
</style>
